import { useEffect, useMemo } from 'react';
import { isValidPaymentSelection } from '../../../types/schedule.types';
import type {
    CollectionRouting,
    PaymentSelection,
    SchedulePreview,
    SchedulePreviewParams,
} from '../../../types/schedule.types';
import type { SchedulingMethod } from '@/features/products/types/products.types';
import type { PaymentPreviewBlockReason } from '../../../utils/stepGate';
import {
    buildCommittableSelection,
    collectionRoutingEqual,
    derivePaymentPreviewBlockReason,
    schedulePreviewEqual,
} from './derive';

/** Owns commit orchestration and step gating: what counts as committable, the sole write
 *  into `paymentSelection`, and the reason (if any) reported to block `Next`. */
export function usePaymentSelectionCommit(options: {
    isReadOnly: boolean;
    selectedMethod: SchedulingMethod | null;
    params: SchedulePreviewParams | null;
    data: SchedulePreview | undefined;
    error: Error | null;
    isFetching: boolean;
    isPlaceholderData: boolean;
    anchor: string;
    recordedLeadMonths: number | undefined;
    paymentSelection: PaymentSelection | undefined;
    isCurrentSelectionConfirmed: boolean;
    updatePaymentSelection: (selection: PaymentSelection) => void;
    collectionRouting: CollectionRouting | undefined;
    isDealerConfigUnresolved: boolean;
    isDealerReceiptIncomplete: boolean;
    methods: SchedulingMethod[];
    selectedMethodKey: string | null;
    unavailableReasons: Readonly<Record<string, string>>;
    hasDepositCeilingRejection: boolean;
    hasLeadRejection: boolean;
    hasPastServiceDate: boolean;
    isDepositWriteBackPending: boolean;
    reportPaymentPreviewBlocked: (reason: PaymentPreviewBlockReason | null) => void;
}): {
    isLiveScheduleRecorded: boolean;
    previewBlockReason: PaymentPreviewBlockReason | null;
} {
    const {
        isReadOnly,
        selectedMethod,
        params,
        data,
        error,
        isFetching,
        isPlaceholderData,
        anchor,
        recordedLeadMonths,
        paymentSelection,
        isCurrentSelectionConfirmed,
        updatePaymentSelection,
        collectionRouting,
        isDealerConfigUnresolved,
        isDealerReceiptIncomplete,
        methods,
        selectedMethodKey,
        unavailableReasons,
        hasDepositCeilingRejection,
        hasLeadRejection,
        hasPastServiceDate,
        isDepositWriteBackPending,
        reportPaymentPreviewBlocked,
    } = options;

    const committableSelection = useMemo(
        () =>
            buildCommittableSelection({
                isReadOnly,
                selectedMethod,
                params,
                data,
                error,
                isFetching,
                isPlaceholderData,
                anchor,
                recordedLeadMonths,
                collectionRouting,
                isDealerConfigUnresolved,
                isDealerReceiptIncomplete,
            }),
        [
            isReadOnly,
            selectedMethod,
            params,
            data,
            error,
            isFetching,
            isPlaceholderData,
            anchor,
            recordedLeadMonths,
            collectionRouting,
            isDealerConfigUnresolved,
            isDealerReceiptIncomplete,
        ],
    );

    // Matching params are not enough on resume: elemis re-anchors every schedule on the
    // request date, so identical params can return different rows a day later.
    const isLiveScheduleRecorded =
        isCurrentSelectionConfirmed &&
        committableSelection !== null &&
        isValidPaymentSelection(paymentSelection) &&
        schedulePreviewEqual(paymentSelection.snapshot, committableSelection.snapshot) &&
        collectionRoutingEqual(paymentSelection.collectionRouting, committableSelection.collectionRouting);

    // Sole writer of paymentSelection.
    useEffect(() => {
        if (committableSelection === null || isLiveScheduleRecorded || isDepositWriteBackPending) return;
        updatePaymentSelection(committableSelection);
    }, [committableSelection, isLiveScheduleRecorded, isDepositWriteBackPending, updatePaymentSelection]);

    // Blocks Next while what is on screen cannot commit; all-unavailable blocks with
    // copy pointing at Configure. Stays silent only for carve-outs with no remedy anywhere.
    const previewBlockReason = derivePaymentPreviewBlockReason({
        isReadOnly,
        isLiveScheduleRecorded,
        methods,
        selectedMethodKey,
        hasSelectedMethod: !!selectedMethod,
        unavailableReasons,
        hasDepositCeilingRejection,
        hasLeadRejection,
        hasPastServiceDate,
        hasUncommittablePreview: !!params && committableSelection === null,
        hasIncompleteDealerReceipt: isDealerReceiptIncomplete,
    });

    useEffect(() => {
        reportPaymentPreviewBlocked(previewBlockReason);
        return () => reportPaymentPreviewBlocked(null);
    }, [previewBlockReason, reportPaymentPreviewBlocked]);

    return { isLiveScheduleRecorded, previewBlockReason };
}
